import { useEffect, useState } from "react";
import { useAuth } from "@clerk/clerk-expo";
import { Link } from "expo-router";
import { Pressable, StyleSheet, Text, TextInput, View } from "react-native";

import { fetchMe, renameMe, type Me } from "../src/api";
import { colour, type } from "./theme";

/**
 * Who you are, or a way to sign in.
 *
 * Deliberately shows the name the API returns rather than the one Clerk holds.
 * They are the same today; the point is that this screen reads OUR user row,
 * so the first render after signing in is also the request that creates it
 * (ADR-0005). If that call fails, the account genuinely is not usable yet and
 * saying so beats a name that came from somewhere else.
 */
export function AccountBar() {
  const { isLoaded, isSignedIn, signOut } = useAuth();
  const [me, setMe] = useState<Me | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");

  useEffect(() => {
    if (!isLoaded || !isSignedIn) {
      setMe(null);
      setError(null);
      return;
    }

    const controller = new AbortController();
    fetchMe(controller.signal)
      .then((found) => {
        if (!controller.signal.aborted) setMe(found);
      })
      .catch((cause: Error) => {
        if (controller.signal.aborted || cause.name === "AbortError") return;
        setError(cause.message);
      });

    return () => controller.abort();
  }, [isLoaded, isSignedIn]);

  if (!isLoaded) return null;

  if (!isSignedIn) {
    return (
      <View style={styles.bar}>
        <Text style={styles.muted}>Browsing as a guest</Text>
        <Link href="/sign-in" asChild>
          <Pressable>
            <Text style={styles.action}>Sign in</Text>
          </Pressable>
        </Link>
      </View>
    );
  }

  if (editing) {
    const save = () => {
      const name = draft.trim();
      if (name === "") return;
      setEditing(false);
      renameMe(name)
        .then(setMe)
        .catch((cause: Error) => setError(cause.message));
    };

    return (
      <View style={styles.bar}>
        <TextInput
          style={styles.rename}
          value={draft}
          onChangeText={setDraft}
          onSubmitEditing={save}
          placeholder="What should we call you?"
          placeholderTextColor={colour.inkFaint}
          autoFocus
          returnKeyType="done"
          maxLength={120}
        />
        <View style={styles.actions}>
          <Pressable onPress={save}>
            <Text style={styles.action}>Save</Text>
          </Pressable>
          <Pressable onPress={() => setEditing(false)}>
            <Text style={styles.muted}>Cancel</Text>
          </Pressable>
        </View>
      </View>
    );
  }

  return (
    <View style={styles.bar}>
      <Pressable
        onPress={() => {
          // The name is a guess until somebody says otherwise, so it is
          // editable in place rather than hidden behind a settings screen
          // this app does not have.
          setDraft(me?.displayName ?? "");
          setEditing(true);
        }}
        disabled={!me}
      >
        <Text style={styles.muted} numberOfLines={1}>
          {error ?? (me ? `Signed in as ${me.displayName}` : "Signing in…")}
        </Text>
      </Pressable>
      <View style={styles.actions}>
        <Pressable onPress={() => void signOut()}>
          <Text style={styles.action}>Sign out</Text>
        </Pressable>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  bar: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    // No bottom padding: this sits in the header row beside the mark, and the
    // row owns the spacing. Owning it here too indented the mark by a line.
    gap: 12,
  },
  muted: { color: colour.inkMuted, ...type.meta, flexShrink: 1 },
  actions: { flexDirection: "row", gap: 16 },
  rename: {
    flex: 1,
    borderWidth: 1,
    borderColor: colour.lineStrong,
    borderRadius: 6,
    paddingHorizontal: 8,
    paddingVertical: 6,
    ...type.meta,
    color: colour.ink,
    backgroundColor: colour.surface,
  },
  action: { color: colour.accent, ...type.meta, fontWeight: "600" },
});
